import { groupBy } from "lodash";
import { useMemo } from "react";

import dayjs from "dayjs";

import { UserInfo } from "@/api/rest/models/account.ts";
import { Building } from "@/api/rest/models/building.ts";
import { Household, HouseholdWithOwnership } from "@/api/rest/models/household";

import { useHouseholds } from "./useHouseholds.ts";

const processHouseholds = (households: HouseholdWithOwnership[]): Household[] => {
  return households.map((h) => ({
    ...h,
    warrantyEndDateTimestamp: dayjs(h.warrantyEndDate),
  }));
};

const calculateEnrichedHouseholds = (households: HouseholdWithOwnership[], managedBuildings: Building[]) => {
  const processedHouseholds = processHouseholds(households);
  const householdsByBuilding = groupBy(processedHouseholds, (household) => household.buildingId);

  const managedBuildingsById = new Map(managedBuildings.map((building) => [building.buildingId, building]));

  const buildings = Object.entries(householdsByBuilding).map(([buildingId, householdsInBuilding]) => {
    const managedBuilding = managedBuildingsById.get(Number(buildingId));
    if (managedBuilding) {
      return {
        ...managedBuilding,
        households: householdsInBuilding,
      };
    }

    const firstHousehold = households.find((h) => h.id === householdsInBuilding[0].id)!;
    return {
      ownerAccountIds: [],
      buildingId: firstHousehold.buildingId,
      accountId: firstHousehold.accounts[0]?.id,
      country: "", // These properties are not available on the household object
      city: "",
      postcode: "",
      street: firstHousehold.buildingStreet,
      developmentId: firstHousehold.developmentId,
      developmentName: firstHousehold.developmentName,
      developmentLogoUrl: null,
      developmentLogoMaxWidth: null,
      developmentLogoMaxHeight: null,
      primaryLanguage: "en",
      supportedLanguages: ["en"],
      households: householdsInBuilding,
      partnerCode: undefined,
    };
  });

  return buildings;
};

export const useEnrichedHouseholds = (user: UserInfo | null) => {
  const households = useHouseholds();

  // First memo for managed buildings
  const managedBuildings = useMemo(() => {
    return user?.accounts.flatMap((acc) => acc.managedBuildings) ?? [];
  }, [user?.accounts]);

  // Second memo for the final calculation
  return useMemo(() => {
    return calculateEnrichedHouseholds(households, managedBuildings);
  }, [households, managedBuildings]);
};
